import React from 'react';
import { EntityId } from "@reduxjs/toolkit";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { destroyAddon } from "../../../../actions/mine_craft/addonsActions";
import { AddonInterface, selectAddonEntities, selectAddonIds, setEditId } from "../../../../store/mine_craft/addonsSlice";
import usePermissions from "../../../../hooks/usePermissions";

import { Image, message, Spin } from "antd";
import { LikeOutlined, MessageOutlined, StarOutlined } from "@ant-design/icons";
import CardImage from "../../../../componets/CardImage";
import Pagination from '../../../../componets/Pagination';
import IconText from "../../../../componets/IconText";
import { ButtonDestroy, ButtonDownload, ButtonEdit } from "../../../../componets/Buttons";
import Meta from "../../Users/_partials/_Meta";
import { selectUserEntities } from "../../../../store/mine_craft/usersSlice";
import { formattedDate } from "../../../../helpers/DateHelpers";
import {HiddenTag} from "../../../../componets/Tags/HiddenTag";

export interface AddonListInterface {
  limit: number;
  statsKeys?: string[];
  overflowCount?: number;
}

const _List = (
  { limit, overflowCount = 9, statsKeys = ['likes', 'comments'] }: AddonListInterface
) => {
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();
  const ids = useAppSelector(selectAddonIds);
  const items  = useAppSelector(selectAddonEntities);
  const users = useAppSelector(selectUserEntities);
  const count     = useAppSelector(state => state.mineCraft.addons.count);
  const error     = useAppSelector(state => state.mineCraft.addons.error);
  const status    = useAppSelector(state => state.mineCraft.addons.status);
  const { write_minecraft } = usePermissions();

  function onClickEdit(id: EntityId) {
    dispatch(setEditId(id));
  }

  function onClickDestroy(id: EntityId) {
    handleDestroy(id).catch(console.error);
  }

  async function handleDestroy (id: any) {
    const resultAction = await dispatch(destroyAddon(id));

    if (destroyAddon.fulfilled.match(resultAction)) {
      message.success(`ItemID ${id} was successful deleted`);
      setTimeout(() => navigate(0), 1000);
    }

    if (destroyAddon.rejected.match(resultAction)) {
      message.error(resultAction.error.message);
    }
  }

  const stats = (item: AddonInterface) => {
    const array: React.ReactNode[] = [
      <Image src={item.ico_url} width={12} height={12} preview={false} />
    ];

    statsKeys?.forEach((key: string) => {
      let opts: any = { tooltip: key };

      switch (key) {
        case 'likes':
          opts.icon = LikeOutlined
          opts.text = item.likes_count
          break;
        case 'comments':
          opts.icon = MessageOutlined
          opts.text = item.comments_count
          break;
        case 'challenges':
          opts.icon = StarOutlined
          opts.text = item.challenge_points
          break;
        default:
          opts = null;
      }

      if (opts) {
        array.push(<IconText {...opts} />);
      }
    });

    return array;
  }

  const badge = (rank: number | null) => {
    if (!rank) {
      return;
    }

    let color: string;
    switch (rank) {
      case 1:
        color = '#faad14';
        break;
      case 2:
        color = '#bfbfbf';
        break;
      case 3:
        color = '#cd7f32';
        break;
      default:
        color = '#c4c4c4';
    }

    return {
      className: 'v-rank-badge',
      count: rank,
      color: color,
      title: `Rank ${rank}`,
      overflowCount: overflowCount
    };
  }

  const renderList = (id: EntityId) => {
    const item = items[id];

    if (!item) {
      return;
    }
    const user = users[item.user_id];
    const formattedCreatedAt = formattedDate(item.created_at);

    return (
      <CardImage
        badge={badge(item.challenge_rank || item.virtual_rank)}
        key={item.id}
        size='medium'
        className='v-addon-card'
        image={item.image_url}
        title={item.title}
        link={item.url}
        stats={stats(item)}
        labels={[<HiddenTag key={`show-tag-${item.id}`} hidden={!item.published} text='Unpublished' />]}
        options={[
          ...(item.zip_url ? [<ButtonDownload key={`download-${item.id}`} href={item.zip_url} />] : []),
          ...(write_minecraft ? [
            <ButtonEdit key={`edit-${item.id}`} onClick={() => onClickEdit(item.id)} />,
            <ButtonDestroy key={`destroy-${item.id}`} onClick={() => onClickDestroy(item.id)} />
          ] : [])
        ]}
      >
        {<Meta user={user} size='xsmall' className='v-addon-card--meta-user' />}

        <div className='v-small_text'>
          {formattedCreatedAt && <small>{formattedCreatedAt.date} <small>{formattedCreatedAt.time}</small></small> }
        </div>
      </CardImage>
    )
  }

  if (status === 'loading') {
    return <Spin/>;
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>
  }

  return (
    <>
      <div className='v-addons-list'>
        {ids && ids.map(renderList)}
      </div>

      <Pagination total={count} size={limit} />
    </>
  );
}

export default _List;
